import { z } from "zod";
import { unset } from "./fields";

export const NOTIFICATION_PAGE_SIZE_MAX = 100;
const FILTER_MAX_LENGTH = 200;

const trimmed = (value: unknown) => (typeof value === "string" ? unset(value.trim()) : value);
const filterText = z.preprocess(trimmed, z.string().max(FILTER_MAX_LENGTH).optional());
const createdBound = z.preprocess(trimmed, z.union([z.string().date(), z.string().datetime({ offset: true })]).optional());

export const adminNotificationListQuery = z.object({
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(Number.MAX_SAFE_INTEGER).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).default(20)).transform((size) => Math.min(size, NOTIFICATION_PAGE_SIZE_MAX)),
  search: filterText,
  status: filterText,
  priority: filterText,
  notificationType: filterText,
  createdFrom: createdBound,
  createdTo: createdBound,
});
export type AdminNotificationListQuery = z.infer<typeof adminNotificationListQuery>;

const text = z.string().nullable();

export const adminNotificationRecipient = z.object({ id: z.string(), name: z.string(), email: z.string(), phone: text });
export type AdminNotificationRecipient = z.infer<typeof adminNotificationRecipient>;

export const adminNotificationLog = z.object({
  id: z.string(),
  channel: z.string(),
  status: z.string(),
  externalId: text,
  errorMessage: text,
  sentAt: text,
  createdAt: text,
});
export type AdminNotificationLog = z.infer<typeof adminNotificationLog>;

export const adminNotificationRow = z.object({
  id: z.string(),
  source: z.enum(["queue", "auth"]),
  notificationType: z.string(),
  recipientUserId: text,
  recipientEmail: text,
  recipientPhone: text,
  recipient: adminNotificationRecipient.nullable(),
  entityType: text,
  entityId: text,
  payload: z.record(z.string(), z.unknown()).nullable(),
  priority: text,
  status: text,
  attempts: z.number(),
  maxAttempts: z.number(),
  errorMessage: text,
  scheduledFor: text,
  processedAt: text,
  createdAt: text,
  updatedAt: text,
  logs: z.array(adminNotificationLog),
});
export type AdminNotificationRow = z.infer<typeof adminNotificationRow>;

export const adminNotificationPage = z.object({
  data: z.array(adminNotificationRow),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().positive(),
});
export type AdminNotificationPage = z.infer<typeof adminNotificationPage>;

export const adminNotificationTypeOption = z.object({ value: z.string(), label: z.string() });
export type AdminNotificationTypeOption = z.infer<typeof adminNotificationTypeOption>;

export const adminNotificationTypes = z.array(adminNotificationTypeOption);
